/*
  SC Configurator 3D Viewer
  --------------------------
  Standalone module. Mounts a persistent Three.js canvas inside the
  configurator's media root and reacts to the engine's
  `sc-configurator:change` event (already dispatched by sc-product-configurator.js).

  It is deliberately decoupled: it reads data-* attributes rendered by Liquid,
  loads a GLB per selected size, and toggles named nodes as the customer builds.

  Requires (loaded by the section as ES module imports via importmap):
    three, OrbitControls, GLTFLoader, DRACOLoader, RoomEnvironment

  Node-name contract (must match the Blender export exactly, case-sensitive):
    Body (always on): any name, not toggled
    Toggleable parts: whatever names you map in the theme editor, e.g.
      INSERT-ACRYLIC, INSERT-METAL, BIRDIE-BAR
*/
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

(function () {
  'use strict';

  var DRACO_PATH = 'https://cdn.jsdelivr.net/npm/three@0.165.0/examples/jsm/libs/draco/';

  // Node names treated as the "body" for scaling + pivot. First match wins.
  // Keeps the cube centred even when accessories stick out past it.
  var ANCHOR_NAMES = ['SEED-CUBE', 'BODY_CLEAR', 'BODY', 'Large cube main'];

  // Three.js renders transmissive glass by sampling a buffer that contains ONLY
  // opaque objects. Anything transmissive is excluded, so clear acrylic inside
  // clear acrylic is invisible from outside. Internal acrylic parts therefore
  // get a frosted OPAQUE material so they show through the cube walls, which
  // also matches how they photograph in real life.
  var FROSTED_NODES = ['INSERT-ACRYLIC', 'DIVIDER', 'TRAY', 'Large Easy Tray', 'large acrylic tray left', 'large acrylic tray right', 'Large cube tray wall', 'Small cube tray devider wall', 'small acrillic tray left', 'small acrillic tray right', 'large acrillic tray left', 'large acrillic tray right'];

  // FRAMING TUNING
  // FRAME_PADDING scales with the model, so small builds end up proportionally
  // tighter than large ones. FRAME_MIN_DISTANCE stops the camera closing in too
  // far on the smaller builds (cube alone, cube + trays) while leaving the
  // larger ones (Birdie Bar added) exactly where the padding puts them.
  var FRAME_PADDING = 1.35;
  var FRAME_MIN_DISTANCE = 3.1;

  // SURFACE TUNING — the two lightest elements. Lower = less glaring white.
  // FROSTED_COLOUR   internal acrylic (trays, divider)
  // LOGO_COLOUR      the engraved Seed Cube logo
  var FROSTED_COLOUR = 0xbccbd6;
  // Middle ground between matte (reads as painted card) and mirror-glossy
  // (reads as hard opaque plastic). Softer highlights suggest light passing
  // into the material without needing actual transparency.
  var FROSTED_ROUGHNESS = 0.22;
  var LOGO_COLOUR    = 0xc4cfd6;

  // Internal acrylic (trays, divider) MUST stay fully opaque. Three.js builds
  // the transmission buffer from opaque objects only, so anything alpha-blended
  // drops out of it and becomes invisible when viewed through the clear walls.
  // Tested at 0.82: visible through the front opening, gone from outside.
  // Translucency is therefore faked below with sheen + a faint emissive lift,
  // which survives the opaque requirement.
  var FROSTED_OPACITY = 1.0;

  // MATERIAL MODE — the single biggest visual decision.
  //   'transmission' : walls use physically-simulated glass (refraction, real
  //                    depth). Internal parts MUST be opaque, because three.js
  //                    excludes transparent objects from the buffer the walls
  //                    sample. Best-looking walls, solid trays.
  //   'alpha'        : everything uses alpha blending and sorts by depth, so
  //                    walls AND trays can both be see-through. Loses
  //                    refraction, gains real transparency throughout.
  //   TESTED AND REJECTED: 'alpha' gives real transparency throughout but
  //   loses refraction (walls read flat) and, with depthWrite off on
  //   double-sided geometry, the trays show their own back faces as visible
  //   mesh structure. No tuning removes that. 'transmission' is the best
  //   available result; opaque internal parts are the price.
  var MATERIAL_MODE = 'transmission';

  // Alpha-mode opacities. Lower = more see-through.
  var ALPHA_WALL_OPACITY  = 0.26;
  var ALPHA_TRAY_OPACITY  = 0.5;

  // Alpha mode loses refraction, which is what made the walls read as glass.
  // Most of the remaining "glass" cue is REFLECTION: sharp environment
  // highlights and crisp edge specular. Push these to recover the look.
  var ALPHA_GLASS_ENV       = 2.4;   // environment reflection strength
  var ALPHA_GLASS_CLEARCOAT = 1.0;   // hard polished top layer

  // Applies to 'transmission' mode only.
  // Internal acrylic must be FULLY OPAQUE there. Two mechanisms were tested
  // and both make the trays invisible when viewed through the clear walls:
  //   transparent:true / opacity 0.82  -> alpha-blended objects are excluded
  //                                       from the transmission buffer
  //   transmission 0.35                -> transmissive objects are likewise not
  //                                       rendered into the buffer other
  //                                       transmissive surfaces sample from
  // Nested transparency is not supported here. Do not re-test.
  // Translucency is instead suggested by a POLISHED finish: low roughness and
  // strong clearcoat read as acrylic, where matte + emissive read as painted card.
  var FROSTED_TRANSMISSION = 0;
  var FROSTED_SHEEN = 0.65;
  var FROSTED_GLOW  = 0;

  // Logo surface. Higher clearcoat/env = more sheen, which past a point reads
  // as a halo around the letterforms rather than crisp engraving.
  var LOGO_ROUGHNESS = 0.5;
  var LOGO_CLEARCOAT = 0.08;
  var LOGO_ENV       = 0.35;

  // Acrylic wall thickness drives how much light bends around anything behind
  // it. Too high and engraved detail picks up a soft glow. Too low and the
  // walls stop reading as solid material.
  var ACRYLIC_THICKNESS = 0.12;

  // Nodes treated as the logo, so its tone is controlled here rather than
  // depending on whatever material survived the Blender export.
  var LOGO_NODES = ['LOGO'];

  // Returns null for blank/invalid so callers can fall back to the default.
  function numOrNull(value) {
    if (value === null || value === undefined || value === '') return null;
    var n = parseFloat(value);
    return isNaN(n) ? null : n;
  }

  function parseNodeList(value) {
    return String(value || '')
      .split(',')
      .map(function (s) { return s.trim(); })
      .filter(Boolean);
  }

  function Viewer(root) {
    this.root = root;
    this.mount = root.querySelector('[data-scpc-3d]');
    if (!this.mount) return;

    this.cfg = {
      defaultModel: this.mount.getAttribute('data-3d-default') || '',
      autoRotate: this.mount.getAttribute('data-3d-autorotate') !== 'false',
      background: this.mount.getAttribute('data-3d-bg') || '#d8dde0',
      // Nodes that must ALWAYS start hidden, whether or not an option currently
      // maps to them. Without this, removing or emptying an option's node-name
      // field leaves its part permanently visible, because the viewer only
      // learns a node is optional by seeing it in the map.
      optionalNodes: parseNodeList(this.mount.getAttribute('data-3d-optional-nodes')),
      // Hidden ONLY while the default (hero) model is showing (v47).
      heroHiddenNodes: parseNodeList(this.mount.getAttribute('data-3d-hero-hidden')),

      // Per-section material overrides. Blank means "use the module default".
      // A clear tray inside a cube and the same tray on its own need very
      // different settings, so these are exposed to the theme editor.
      clearStyle: this.mount.getAttribute('data-3d-clear-style') || '',
      clearTint: this.mount.getAttribute('data-3d-clear-tint') || '',
      clearSolidity: numOrNull(this.mount.getAttribute('data-3d-clear-solidity')),
      frostedTint: this.mount.getAttribute('data-3d-frosted-tint') || '',
      envStrength: numOrNull(this.mount.getAttribute('data-3d-env')),

      // Load zoom as a percentage. 100 = the module's default framing,
      // higher = closer. Null means "use the default".
      zoom: numOrNull(this.mount.getAttribute('data-3d-zoom')),

      // Background. Style 'flat' uses the base colour alone; 'gradient' uses
      // the explicit top/bottom stops when given, otherwise derives them from
      // the base colour. Contrast behind clear acrylic is what makes it read.
      bgStyle: this.mount.getAttribute('data-3d-bg-style') || 'gradient',
      bgTop: this.mount.getAttribute('data-3d-bg-top') || '',
      bgBottom: this.mount.getAttribute('data-3d-bg-bottom') || '',

      // Are these parts being viewed INSIDE the clear cube?
      // Inside, three.js excludes transparent objects from the buffer the walls
      // sample, so internal acrylic must be forced opaque to stay visible.
      // Standalone there is nothing in front of them, so that compromise is
      // pure downside and the authored glass materials should be used as-is.
      nested: this.mount.getAttribute('data-3d-nested') !== 'false'
    };

    // Read the option->model and option->nodes maps rendered by Liquid.
    // Each is a JSON blob in a data attribute.
    this.sizeModels = this.readJson('[data-scpc-3d-models]');   // { small: url, large: url }
    this.nodeMap    = this.readJson('[data-scpc-3d-nodes]');    // { "containment.tray": ["INSERT-ACRYLIC"], ... }
    this.viewMap    = this.readJson('[data-scpc-3d-views]');    // { "containment.tray": { zoom, azimuth, elevation } }
    this.viewStack  = [];      // selection keys in the order they were chosen
    this.activeView = null;    // view of the most recently chosen option that has one

    this.modelCache = {};      // url -> gltf.scene (cloned per use)
    this.currentUrl = '';
    this.nodes = {};           // name -> Object3D for the loaded model
    this.allToggleNames = this.collectToggleNames();
    this.active = false;
    this.raf = 0;
    this.built = false;
    this.destroyed = false;
    this.cleanups = [];

    this.bind();

    // Theme setting: open in 3D by default. Deferred until the viewer scrolls
    // into view so the model download never blocks initial page LCP.
    if (this.mount.getAttribute('data-3d-open-default') === 'true') this.autoOpen3D();
  }

  Viewer.prototype.readJson = function (selector) {
    var el = this.root.querySelector(selector);
    if (!el) return {};
    try { return JSON.parse(el.textContent || '{}'); }
    catch (e) { return {}; }
  };

  Viewer.prototype.collectToggleNames = function () {
    var names = {};
    var map = this.nodeMap || {};
    Object.keys(map).forEach(function (k) {
      (map[k] || []).forEach(function (n) { names[n] = true; });
    });
    // Merge the always-optional list so parts stay hidden even when no option
    // currently maps to them (e.g. a choice temporarily removed or hidden).
    (this.cfg.optionalNodes || []).forEach(function (n) { names[n] = true; });
    return Object.keys(names);
  };

  // ---- lifecycle -----------------------------------------------------------

  Viewer.prototype.bind = function () {
    var self = this;
    // React to every selection change from the engine. Listen on document so we
    // catch the event regardless of which element the engine dispatches it on.
    this.on(document, 'sc-configurator:change', function (e) {
      self.onChange(e.detail || {});
    });
    // Single 3D-mode toggle: flips between photos and 3D.
    this.on(this.root, 'click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-scpc-3d-toggle]');
      if (!btn) return;
      e.preventDefault();
      var mode = btn.getAttribute('data-scpc-3d-toggle');
      // 'toggle' = single button; 'on'/'off' kept for backwards compatibility.
      if (mode === 'on') self.setActive(true);
      else if (mode === 'off') self.setActive(false);
      else self.setActive(!self.active);
    });
    // "See your build in 3D" jump link. It lives outside the viewer (below the
    // purchase bar), so listen on document and confirm it belongs to this section.
    this.on(document, 'click', function (e) {
      var jump = e.target.closest && e.target.closest('[data-scpc-3d-jump]');
      if (!jump) return;
      var section = self.root.closest('.scpc') || self.root;
      if (section && !section.contains(jump)) return;   // a different configurator on the page
      e.preventDefault();
      self.jumpToViewer();
    });

    // Pause rendering when tab hidden.
    this.on(document, 'visibilitychange', function () {
      if (document.hidden) self.stopLoop();
      else if (self.active) self.startLoop();
    });
  };

  // Theme setting "Open in 3D by default": enter 3D once the viewer scrolls
  // into view, so the model download never blocks initial page load / LCP.
  Viewer.prototype.autoOpen3D = function () {
    var self = this;
    var go = function () { if (!self.destroyed && !self.active) self.setActive(true); };
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) { io.disconnect(); go(); return; }
        }
      }, { rootMargin: '200px' });
      io.observe(this.root);
      this.cleanups.push(function () { io.disconnect(); });
    } else {
      window.setTimeout(go, 300);
    }
  };

  Viewer.prototype.on = function (target, name, handler, opts) {
    target.addEventListener(name, handler, opts || false);
    this.cleanups.push(function () { target.removeEventListener(name, handler, opts || false); });
  };

  // Fire a tracking event so engagement with the 3D viewer is measurable.
  Viewer.prototype.track = function (action) {
    var payload = {
      event: 'sc_3d_viewer',
      action: action,                                  // 'open' | 'close'
      builderKey: this.root.getAttribute('data-builder-key') || '',
      sectionId: this.root.getAttribute('data-section-id') || '',
      modelUrl: this.currentUrl || this.cfg.defaultModel || ''
    };
    // GTM / dataLayer
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    } catch (e) { /* no dataLayer on this store */ }
    // GA4 direct (if gtag is present)
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'sc_3d_viewer', {
          action: action,
          builder_key: payload.builderKey,
          model_url: payload.modelUrl
        });
      }
    } catch (e) { /* gtag not present */ }
    // Shopify analytics custom event
    try {
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.lib && window.ShopifyAnalytics.lib.track) {
        window.ShopifyAnalytics.lib.track('3D Viewer ' + action, payload);
      }
    } catch (e) { /* analytics lib not ready */ }
    // Always dispatch a DOM event so any other script can listen.
    try {
      this.root.dispatchEvent(new CustomEvent('sc-configurator:3d-' + action, {
        bubbles: true, detail: payload
      }));
    } catch (e) { /* older browser */ }
  };

  // The engine dispatches its first change event at startup. If this module
  // initialises after that, we never receive it and lastDetail stays undefined,
  // leaving the viewer blank until the shopper changes something. Read the
  // current state straight from the rendered DOM as a fallback.
  Viewer.prototype.detailFromDOM = function () {
    var section = this.root.closest('.scpc') || document;
    var selections = {};

    // Single/multi option cards carry their step, value and selected state.
    var cards = section.querySelectorAll('[data-scpc-option-card]');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var step = card.getAttribute('data-step-key');
      var val = card.getAttribute('data-value');
      if (!step || !val) continue;
      if (card.getAttribute('data-scpc-selected') === 'true') selections[step] = val;
    }

    // Quantity add-ons, keyed the same way the node map expects.
    var qty = section.querySelectorAll('[data-scpc-quantity]');
    for (var q = 0; q < qty.length; q++) {
      var qc = qty[q];
      var qstep = qc.getAttribute('data-step-key');
      var qkey = qc.getAttribute('data-quantity-key');
      var out = qc.querySelector('[data-scpc-quantity-value]');
      var n = out ? parseInt(out.textContent, 10) : 0;
      if (qstep && qkey && n > 0) selections[qstep] = qkey;
    }

    return {
      selections: selections,
      connectedValue: section.getAttribute && section.getAttribute('data-seedcube-size') || ''
    };
  };

  // Scroll the viewer back into view and switch it into 3D.
  Viewer.prototype.jumpToViewer = function () {
    var self = this;
    var target = this.root.querySelector('[data-scpc-viewer]') || this.mount;
    if (!target) return;

    // Turn 3D on first so the canvas is sized and rendering by the time we arrive.
    this.setActive(true);
    this.track('jump');

    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    try {
      target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
    } catch (err) {
      target.scrollIntoView();
    }

    // Nudge the renderer after the scroll settles, in case layout shifted.
    window.setTimeout(function () { if (!self.destroyed) self.resize(); }, 450);
  };

  Viewer.prototype.setActive = function (on) {
    var changed = this.active !== !!on;
    this.active = !!on;
    this.mount.hidden = !on;
    this.root.classList.toggle('scpc--3d-active', this.active);
    // The section root also carries scpc--3d-active (see below) so CSS can hide the
    // photo media + carousel while 3D is on. Toggle it on the section too.
    var section = this.root.closest('.scpc') || this.root;
    if (section) section.classList.toggle('scpc--3d-active', this.active);
    var btns = this.root.querySelectorAll('[data-scpc-3d-toggle]');
    for (var i = 0; i < btns.length; i++) {
      var btn = btns[i];
      var mode = btn.getAttribute('data-scpc-3d-toggle');
      if (mode === 'toggle') {
        // Single button: label says what tapping will do next.
        btn.setAttribute('aria-pressed', this.active ? 'true' : 'false');
        btn.classList.toggle('is-active', this.active);
        var labelEl = btn.querySelector('[data-scpc-3d-label]');
        var next = this.active
          ? (btn.getAttribute('data-label-off') || 'Photos')
          : (btn.getAttribute('data-label-on') || 'See it in 3D');
        if (labelEl) labelEl.textContent = next;
      } else {
        var isOn = mode === 'on';
        var pressed = (isOn && this.active) || (!isOn && !this.active);
        btn.setAttribute('aria-pressed', pressed ? 'true' : 'false');
        btn.classList.toggle('is-active', pressed);
      }
    }
    // Once used, stop the attention pulse for the rest of the session.
    if (this.active) {
      for (var b = 0; b < btns.length; b++) btns[b].classList.add('has-been-used');
    }

    if (changed) this.track(this.active ? 'open' : 'close');

    if (this.active) {
      if (!this.built) this.build();
      // Container is now visible; size the renderer to it before first frame.
      var self = this;
      requestAnimationFrame(function () {
        self.resize();
        if (!self.lastDetail) self.lastDetail = self.detailFromDOM();
        self.applyState(self.lastDetail);
        self.startLoop();
      });
    } else {
      this.stopLoop();
    }
  };

  // Build a soft vertical gradient from the configured base colour: darker at
  // the top, lighter at the bottom. Gives clear acrylic something to refract.
  Viewer.prototype.makeGradientBackground = function (baseHex) {
    var base = new THREE.Color(baseHex || '#d8dde0');

    // Flat background: no gradient at all. Cheapest, but clear acrylic has
    // less to separate against.
    if (this.cfg.bgStyle === 'flat') return base;

    var hsl = { h: 0, s: 0, l: 0 };
    base.getHSL(hsl);

    var top, mid, bot;
    if (this.cfg.bgTop && this.cfg.bgBottom) {
      // Explicit stops from the theme editor.
      top = new THREE.Color(this.cfg.bgTop);
      bot = new THREE.Color(this.cfg.bgBottom);
      mid = top.clone().lerp(bot, 0.55);
    } else {
      // Derived: darker above, lighter below, hue and saturation preserved.
      top = new THREE.Color().setHSL(hsl.h, hsl.s, Math.max(0, hsl.l - 0.30));
      mid = new THREE.Color().setHSL(hsl.h, hsl.s, Math.max(0, hsl.l - 0.08));
      bot = new THREE.Color().setHSL(hsl.h, hsl.s, Math.min(1, hsl.l + 0.10));
    }

    var c = document.createElement('canvas');
    c.width = 32; c.height = 512;
    var ctx = c.getContext('2d');
    var g = ctx.createLinearGradient(0, 0, 0, 512);
    g.addColorStop(0.00, '#' + top.getHexString());
    g.addColorStop(0.55, '#' + mid.getHexString());
    g.addColorStop(1.00, '#' + bot.getHexString());
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 32, 512);

    var tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    this._bgTexture = tex;
    return tex;
  };

  Viewer.prototype.build = function () {
    var self = this;
    this.scene = new THREE.Scene();
    // Clear acrylic is only visible against CONTRAST. A flat background makes
    // transmissive glass render the same colour as the backdrop and disappear.
    // A vertical gradient gives the walls something to refract, so edges read.
    this.scene.background = this.makeGradientBackground(this.cfg.background);

    var w = this.mount.clientWidth || this.mount.parentElement && this.mount.parentElement.clientWidth || 600;
    var h = this.mount.clientHeight || w;
    this.camera = new THREE.PerspectiveCamera(40, w / h, 0.01, 100);
    // FRAMING: tuned by Russ. Zoom out = scale all three up together.
    // Distance from origin must stay between controls.minDistance and maxDistance below.
    this.camera.position.set(2.6, 1.8, 3.4);

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setSize(w, h);
    // Fine geometry like the engraved logo is the first thing to soften when
    // rendering below native resolution. Allow full DPR on desktop; stay
    // capped on mobile where the GPU/battery cost matters more than crispness.
    var isSmall = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, isSmall ? 1.5 : 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    // ACESFilmic is a film curve and visibly desaturates strong colours, which
    // washes out the coloured acrylic. Khronos PBR Neutral is designed for
    // product viewers and holds saturation. Fall back if the build is older.
    this.renderer.toneMapping = THREE.NeutralToneMapping || THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.mount.appendChild(this.renderer.domElement);

    this.pmrem = new THREE.PMREMGenerator(this.renderer);
    this.scene.environment = this.pmrem.fromScene(new RoomEnvironment(), 0.04).texture;

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;

    // v77: real engagement, once per viewer. OrbitControls fires 'start' only on
    // genuine user input (drag, pinch, scroll zoom). Auto-rotate and programmatic
    // reframing move the camera via update()/'change' and never fire 'start', so
    // a passive shopper watching the model spin is correctly counted as no
    // interaction. Routed through track() so it also emits sc_3d_viewer with
    // action "interact" alongside the DOM event the configurator listens for.
    var scpcInteractSelf = this;
    this.controls.addEventListener('start', function scpcOnFirstInteract() {
      scpcInteractSelf.controls.removeEventListener('start', scpcOnFirstInteract);
      try { scpcInteractSelf.track('interact'); } catch (error) { /* analytics only */ }
    });
    this.controls.autoRotate = this.cfg.autoRotate;
    this.controls.autoRotateSpeed = 1.0;
    this.controls.minPolarAngle = 0;
    this.controls.maxPolarAngle = Math.PI / 1.8;
    // Wide enough that automatic reframing is never clamped.
    this.controls.minDistance = 0.8;
    this.controls.maxDistance = 12;

    this.scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    var key = new THREE.DirectionalLight(0xffffff, 2.0); key.position.set(2, 4, 2); this.scene.add(key);
    var rim = new THREE.DirectionalLight(0xffffff, 2.6); rim.position.set(-1, 0.5, -3); this.scene.add(rim);
    // Second rim from the opposite side: clear acrylic is read almost entirely
    // through its lit edges, so two rims make the silhouette legible.
    var rim2 = new THREE.DirectionalLight(0xffffff, 1.6); rim2.position.set(3, 1, -2); this.scene.add(rim2);

    this.draco = new DRACOLoader();
    this.draco.setDecoderPath(DRACO_PATH);
    this.loader = new GLTFLoader();
    this.loader.setDRACOLoader(this.draco);

    // Clear acrylic. NOTE: transmission materials must NOT set transparent:true.
    // transparent:true moves the mesh into the transparent render queue, which
    // skips three.js's transmission pass and makes the glass render as a flat
    // surface. Use transmission for see-through, never opacity.
    this.acrylic = (MATERIAL_MODE === 'alpha')
      ? new THREE.MeshPhysicalMaterial({
          color: new THREE.Color(0xffffff),
          metalness: 0,
          // Mirror-smooth: any roughness blurs the highlights that sell glass.
          roughness: 0.0,
          transmission: 0,
          transparent: true,
          opacity: ALPHA_WALL_OPACITY,
          // No depth write, so panels behind (trays, far walls) are not culled.
          depthWrite: false,
          clearcoat: ALPHA_GLASS_CLEARCOAT,
          clearcoatRoughness: 0.0,
          // Strong reflections plus full specular are what remain of the glass
          // cue once refraction is gone.
          envMapIntensity: ALPHA_GLASS_ENV,
          specularIntensity: 1.0,
          reflectivity: 1.0,
          ior: 1.49,
          side: THREE.DoubleSide
        })
      : new THREE.MeshPhysicalMaterial({
      color: new THREE.Color(0xffffff),
      metalness: 0,
      roughness: 0.03,
      transmission: 1.0,
      ior: 1.49,
      thickness: ACRYLIC_THICKNESS,
      // Transmission blur scales with roughness. Keep this low or anything seen
      // through the walls (the far-side logo) reads as smeared.
      specularIntensity: 0.6,
      clearcoat: 0.15,
      clearcoatRoughness: 0.1,
      envMapIntensity: 1.0,
      side: THREE.DoubleSide
    });
    this.metal = new THREE.MeshStandardMaterial({
      color: new THREE.Color(0xc9ccce), metalness: 1.0, roughness: 0.32, envMapIntensity: 1.2
    });

    // ---- Per-section overrides (theme editor) ----------------------------
    // Applied after construction so both MATERIAL_MODE branches are covered.
    // 'glass'   : see-through. Right when nothing sits behind it.
    // 'frosted' : opaque and milky. Right when it must show through the walls.
    // 'tinted'  : opaque with colour. Most legible on a plain background.
    this.applyMaterialOverrides = function () {
      var c = this.cfg;
      var m = this.acrylic;

      if (c.clearTint) { try { m.color.set(c.clearTint); } catch (e) {} }

      if (c.clearStyle === 'frosted') {
        m.transmission = 0;
        m.transparent = false;
        m.opacity = 1;
        m.roughness = 0.3;
      } else if (c.clearStyle === 'tinted') {
        m.transmission = 0;
        m.transparent = false;
        m.opacity = 1;
        m.roughness = 0.12;
      }

      // Solidity 0-100. Higher = easier to see. Drives transmission when the
      // material is see-through, and lightness when it is not.
      if (c.clearSolidity !== null) {
        var s = Math.max(0, Math.min(100, c.clearSolidity)) / 100;
        if (m.transmission > 0 || c.clearStyle === 'glass' || c.clearStyle === '') {
          m.transmission = 1 - (s * 0.85);
          m.thickness = ACRYLIC_THICKNESS + (s * 0.5);
        }
      }

      if (c.frostedTint) { try { this.frosted.color.set(c.frostedTint); } catch (e) {} }

      if (c.envStrength !== null) {
        this.acrylic.envMapIntensity = c.envStrength;
        this.frosted.envMapIntensity = c.envStrength * 0.8;
        this.metal.envMapIntensity = c.envStrength;
      }

      this.acrylic.needsUpdate = true;
      this.frosted.needsUpdate = true;
    };

    // Frosted acrylic: OPAQUE on purpose (see FROSTED_NODES note above).
    // Reads as translucent white without being excluded from the transmission
    // buffer, so it stays visible through the clear walls.
    this.frosted = (MATERIAL_MODE === 'alpha')
      ? new THREE.MeshPhysicalMaterial({
          color: new THREE.Color(FROSTED_COLOUR),
          metalness: 0.0,
          roughness: 0.02,
          transparent: true,
          opacity: ALPHA_TRAY_OPACITY,
          depthWrite: false,
          clearcoat: ALPHA_GLASS_CLEARCOAT,
          clearcoatRoughness: 0.02,
          envMapIntensity: ALPHA_GLASS_ENV * 0.8,
          specularIntensity: 1.0,
          reflectivity: 1.0,
          ior: 1.49,
          sheen: FROSTED_SHEEN,
          sheenRoughness: 0.55,
          sheenColor: new THREE.Color(0xdff0fa),
          side: THREE.DoubleSide
        })
      : new THREE.MeshPhysicalMaterial({
      color: new THREE.Color(FROSTED_COLOUR),
      metalness: 0.0,
      roughness: FROSTED_ROUGHNESS,
      clearcoat: 0.55,
      clearcoatRoughness: 0.18,
      envMapIntensity: 0.95,
      transparent: FROSTED_OPACITY < 1,
      opacity: FROSTED_OPACITY,
      depthWrite: FROSTED_OPACITY >= 1,
      // Real transmission rather than alpha. Thickness stays low so the tray
      // does not smear whatever sits behind it.
      transmission: FROSTED_TRANSMISSION,
      ior: 1.49,
      thickness: FROSTED_TRANSMISSION > 0 ? 0.08 : 0,
      // Sheen gives a soft scattered edge, the way light catches the rim of a
      // translucent panel. Reads as depth without alpha blending.
      sheen: FROSTED_SHEEN,
      sheenRoughness: 0.55,
      sheenColor: new THREE.Color(0xdff0fa),
      // A faint emissive lift simulates light bleeding through the material,
      // so it looks lit from within rather than like painted card.
      emissive: new THREE.Color(FROSTED_COLOUR),
      emissiveIntensity: FROSTED_GLOW,
      side: THREE.DoubleSide
    });

    // Logo: matte and quiet, so it reads as engraved rather than printed.
    this.logoMat = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color(LOGO_COLOUR),
      metalness: 0.0,
      roughness: LOGO_ROUGHNESS,
      clearcoat: LOGO_CLEARCOAT,
      clearcoatRoughness: 0.3,
      envMapIntensity: LOGO_ENV,
      side: THREE.DoubleSide
    });

    // All materials exist; now apply any theme-editor overrides.
    this.applyMaterialOverrides();

    var ro = new ResizeObserver(function () { self.resize(); });
    ro.observe(this.mount);
    this.cleanups.push(function () { ro.disconnect(); });

    this.built = true;
  };

  Viewer.prototype.resize = function () {
    if (!this.built) return;
    var w = this.mount.clientWidth;
    var h = this.mount.clientHeight;
    if (!w || !h) {
      // Not laid out yet; fall back to parent width and a square.
      w = (this.mount.parentElement && this.mount.parentElement.clientWidth) || 600;
      h = w;
    }
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
  };

  Viewer.prototype.startLoop = function () {
    if (this.raf || !this.built) return;
    var self = this;
    (function loop() {
      self.raf = window.requestAnimationFrame(loop);

      // Smoothly ease the camera distance toward the framing target so parts
      // appearing or disappearing never cause a hard jump.
      if (self.targetDistance || self.targetAngles) {
        var offset = self.camera.position.clone().sub(self.controls.target);
        var sph = new THREE.Spherical().setFromVector3(offset);
        var moving = false;
        if (self.targetDistance) {
          var next = sph.radius + (self.targetDistance - sph.radius) * 0.08;
          if (Math.abs(next - sph.radius) > 0.0005) { sph.radius = next; moving = true; }
          else self.targetDistance = 0; // settled
        }
        if (self.targetAngles) {
          // Shortest arc so -170 -> 170 turns 20 degrees, not 340.
          var dTheta = self.targetAngles.theta - sph.theta;
          dTheta = ((dTheta + Math.PI) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI) - Math.PI;
          var dPhi = self.targetAngles.phi - sph.phi;
          if (Math.abs(dTheta) > 0.002 || Math.abs(dPhi) > 0.002) {
            sph.theta += dTheta * 0.08;
            sph.phi += dPhi * 0.08;
            moving = true;
          } else {
            self.targetAngles = null; // settled
            self.controls.autoRotate = self.cfg.autoRotate;
          }
        }
        if (moving) {
          sph.makeSafe();
          offset.setFromSpherical(sph);
          self.camera.position.copy(self.controls.target).add(offset);
        }
      }

      self.controls.update();
      self.renderer.render(self.scene, self.camera);
    })();
  };
  Viewer.prototype.stopLoop = function () {
    if (this.raf) { window.cancelAnimationFrame(this.raf); this.raf = 0; }
  };

  // ---- model loading + node toggling --------------------------------------

  Viewer.prototype.applyAuthoredMaterials = function (obj) {
    var self = this;
    obj.traverse(function (child) {
      if (!child.isMesh) return;

      // Node name wins over material name, so tone is controlled here rather
      // than depending on how the export assigned (or failed to assign)
      // materials. Walk up the tree since meshes sit under named groups.
      var owner = child;
      var frosted = false;
      var isLogo = false;
      while (owner) {
        var authored = owner.userData && owner.userData.name;
        if (owner.name || authored) {
          if (FROSTED_NODES.indexOf(owner.name) >= 0 || (authored && FROSTED_NODES.indexOf(authored) >= 0)) { frosted = true; break; }
          if (LOGO_NODES.indexOf(owner.name) >= 0 || (authored && LOGO_NODES.indexOf(authored) >= 0)) { isLogo = true; break; }
        }
        owner = owner.parent;
      }
      if (isLogo) { child.material = self.logoMat; return; }
      // The frosted override only exists to survive the cube walls. Standalone,
      // let the part keep its authored material so clear acrylic looks clear.
      if (frosted && self.cfg.nested) { child.material = self.frosted; return; }

      var mat = child.material;
      var n = mat && mat.name;
      if (!n) return;

      // Exact 'perspex' = the clear cube walls.
      if (n === 'perspex') { child.material = self.acrylic; return; }
      if (n === 'metal') { child.material = self.metal; return; }

      // Coloured acrylic ('perspex red.001', 'perspex blue.001', ...) is
      // authored transmissive, which excludes it from the transmission buffer
      // and makes it invisible through the walls. Rebuild it as OPAQUE tinted
      // acrylic so it reads correctly from outside, keeping the authored colour.
      if (n.indexOf('perspex') === 0) {
        // Only convert TRANSMISSIVE acrylic. A perspex material with no
        // transmission is already opaque, already renders through the walls,
        // and must be left exactly as authored.
        var isTransmissive = (typeof mat.transmission === 'number' && mat.transmission > 0);
        // Converting to opaque is only needed inside the cube. Standalone, keep
        // the authored transmission so the discs read as coloured glass.
        if (isTransmissive && self.cfg.nested) child.material = self.tintedAcrylic(mat);
        return;
      }
      // 'white' logo and anything else keeps its authored material
    });
  };

  // Bounding box of VISIBLE geometry only. Box3.setFromObject() ignores the
  // visible flag, so hidden accessories would still inflate the bounds and the
  // camera would never tighten back in when they are toggled off.
  Viewer.prototype.visibleBounds = function (root) {
    var box = new THREE.Box3();
    var tmp = new THREE.Box3();
    var any = false;
    root.updateMatrixWorld(true);
    root.traverse(function (child) {
      if (!child.isMesh || !child.geometry) return;
      // Walk up: any hidden ancestor means this mesh is not on screen.
      var o = child;
      while (o) { if (o.visible === false) return; o = o.parent; }
      if (!child.geometry.boundingBox) child.geometry.computeBoundingBox();
      tmp.copy(child.geometry.boundingBox).applyMatrix4(child.matrixWorld);
      box.union(tmp);
      any = true;
    });
    return any ? box : null;
  };

  // Distance the camera needs to sit at for `box` to fit the current viewport,
  // with a little breathing room.
  Viewer.prototype.distanceFor = function (box) {
    var size = box.getSize(new THREE.Vector3());
    var maxDim = Math.max(size.x, size.y, size.z);
    var fov = this.camera.fov * (Math.PI / 180);
    var dist = (maxDim / 2) / Math.tan(fov / 2);
    // Account for narrow viewports, where horizontal fit is the constraint.
    if (this.camera.aspect < 1) dist /= this.camera.aspect;
    // A section-level zoom overrides both the padding and the distance floor,
    // so a merchant can frame a single product as tightly as they like. The
    // floor only exists to stop the multi-part configurator sitting too close.
    // An option-level view zoom (v45) beats the section-level zoom.
    var zoom = (this.activeView && this.activeView.zoom > 0) ? this.activeView.zoom : this.cfg.zoom;
    if (zoom !== null && zoom > 0) {
      var pad = FRAME_PADDING * (100 / zoom);
      return dist * pad;
    }
    // Floor keeps smaller builds from sitting too close; larger builds exceed
    // the floor naturally and are governed by the padding alone.
    return Math.max(dist * FRAME_PADDING, FRAME_MIN_DISTANCE);
  };

  // Ease the camera to fit whatever is currently visible. Called after every
  // node toggle so adding the Birdie Bar gently pulls the view back.
  Viewer.prototype.reframe = function () {
    if (!this.built || !this.current) return;
    var box = this.visibleBounds(this.current);
    if (!box) return;
    this.targetDistance = this.distanceFor(box);

    // Option-level camera angle (v45). Spherical: phi from +Y (0 = top),
    // theta around Y from +Z (0 = front). Autorotate pauses while easing.
    var v = this.activeView;
    if (v && typeof v.azimuth === 'number' && typeof v.elevation === 'number') {
      var deg = Math.PI / 180;
      this.targetAngles = {
        theta: v.azimuth * deg,
        phi: Math.max(0.01, Math.min(this.controls.maxPolarAngle, (90 - v.elevation) * deg))
      };
      this.controls.autoRotate = false;
    }
  };

  // Opaque tinted acrylic built from an authored transmissive material.
  // Cached per source colour so repeated loads don't leak materials.
  Viewer.prototype.tintedAcrylic = function (source) {
    this._tintCache = this._tintCache || {};

    var col = (source && source.color) ? source.color.clone() : new THREE.Color(0xcccccc);

    // Very dark authored colours (a near-black 'green' exists in the current
    // export) would render as a black lump once opaque. Lift them into a
    // readable range while keeping the hue.
    var hsl = { h: 0, s: 0, l: 0 };
    col.getHSL(hsl);
    // Safety net only: a TRANSMISSIVE material authored almost black would
    // render as a dark lump once converted to opaque. Already-opaque materials
    // never reach here, so authored colours are preserved untouched.
    if (hsl.l < 0.20) col.setHSL(hsl.h, Math.max(hsl.s, 0.90), 0.42);

    var key = col.getHexString();
    if (this._tintCache[key]) return this._tintCache[key];

    var m = new THREE.MeshPhysicalMaterial({
      color: col,
      metalness: 0.0,
      roughness: 0.12,
      // Heavy clearcoat + environment lays a white sheen over the tint and
      // mutes it. Keep both modest so the colour stays the dominant read.
      clearcoat: 0.45,
      clearcoatRoughness: 0.14,
      envMapIntensity: 0.75,
      side: THREE.DoubleSide
    });
    this._tintCache[key] = m;
    return m;
  };

  Viewer.prototype.loadModel = function (url, done) {
    var self = this;
    if (!url) { done(null); return; }
    if (this.modelCache[url]) { done(this.modelCache[url]); return; }
    this.loader.load(url, function (gltf) {
      self.applyAuthoredMaterials(gltf.scene);

      // Scale so the BODY fills a consistent size, ignoring accessories that
      // stick out. Falls back to the whole scene if the anchor node is missing.
      var anchor = null;
      gltf.scene.traverse(function (c) {
        if (anchor) return;
        if (c.name && ANCHOR_NAMES.indexOf(c.name) >= 0) { anchor = c; return; }
        var authored = c.userData && c.userData.name;
        if (authored && ANCHOR_NAMES.indexOf(authored) >= 0) anchor = c;
      });
      // IMPORTANT: Box3.setFromObject() does NOT refresh parent matrices. When
      // measuring a CHILD (the anchor) after scaling its parent, the child's
      // world matrix is stale and the measurement comes back in the wrong
      // space. Force a full world-matrix update before every measurement.
      gltf.scene.updateMatrixWorld(true);

      // Scale from the FULL scene (including parts that are currently hidden)
      // so an accessory hanging outside the cube never gets clipped, and so the
      // framing stays identical as parts toggle on and off.
      var box = new THREE.Box3().setFromObject(gltf.scene);
      var size = box.getSize(new THREE.Vector3());
      // FRAMING: model size in the scene. Leave at 2.0 and use the camera
      // position above to change zoom, so lighting/thickness stay consistent.
      var scale = 2.0 / Math.max(size.x, size.y, size.z);
      gltf.scene.scale.setScalar(scale);

      // Re-sync world matrices now that the scale changed, then centre on the
      // BODY. Centring on the whole bounding box lets a protruding accessory
      // (birdie bar) drag the pivot off the cube.
      gltf.scene.updateMatrixWorld(true);
      var box2 = new THREE.Box3().setFromObject(anchor || gltf.scene);
      var c2 = box2.getCenter(new THREE.Vector3());
      gltf.scene.position.sub(c2);
      gltf.scene.updateMatrixWorld(true);
      self.modelCache[url] = gltf.scene;
      done(gltf.scene);
    }, undefined, function () { done(null); });
  };

  Viewer.prototype.showModel = function (url) {
    var self = this;
    if (url === this.currentUrl) return;
    this.loadModel(url, function (obj) {
      if (self.destroyed || !obj) return;
      if (self.current) self.scene.remove(self.current);
      self.current = obj;
      self.nodes = {};
      obj.traverse(function (child) {
        if (child.name) self.nodes[child.name] = child;
        // GLTFLoader sanitises names (spaces -> underscores, dots stripped) but
        // keeps the authored name in userData. Register it so theme-editor
        // fields can use Blender names exactly as typed.
        if (child.userData && child.userData.name) self.nodes[child.userData.name] = child;
      });
      // hide every known toggle node by default
      self.allToggleNames.forEach(function (n) { if (self.nodes[n]) self.nodes[n].visible = false; });
      // Hero-only hides. Size models are separate scene objects, so nothing
      // here leaks into them; the hero's own cached scene keeps the state.
      if (url === self.cfg.defaultModel) {
        (self.cfg.heroHiddenNodes || []).forEach(function (n) { if (self.nodes[n]) self.nodes[n].visible = false; });
      }
      self.scene.add(obj);
      self.currentUrl = url;
      if (!self.lastDetail) self.lastDetail = self.detailFromDOM();
      // A freshly loaded model must always be framed, whatever was on screen before.
      self._lastViewSignature = '';
      self.applyState(self.lastDetail);
      // Fixed viewers have no selections to trigger a reframe, so do it here.
      self.reframe();
      self._lastViewSignature = self.viewSignature();
    });
  };

  // Given the engine's change detail, decide which model + nodes to show.
  Viewer.prototype.onChange = function (detail) {
    this.lastDetail = detail;
    if (!this.active) return;
    this.applyState(detail);
  };

  Viewer.prototype.applyState = function (detail) {
    if (!this.built) return;
    var self = this;
    var selections = detail.selections || {};

    // 1) Choose the model by the connected size value if present.
    var sizeVal = detail.connectedValue || selections.size || '';
    var url = (this.sizeModels && this.sizeModels[sizeVal]) || this.cfg.defaultModel;
    if (url && url !== this.currentUrl) { this.showModel(url); return; } // showModel re-calls applyState
    if (!this.currentUrl && this.cfg.defaultModel) { this.showModel(this.cfg.defaultModel); return; }

    // 2) Reset all toggle nodes off, then turn on the ones for active selections.
    this.allToggleNames.forEach(function (n) { if (self.nodes[n]) self.nodes[n].visible = false; });

    // nodeMap keys are "stepKey.value". Walk selections and light matching nodes.
    Object.keys(selections).forEach(function (stepKey) {
      var val = selections[stepKey];
      var values = Array.isArray(val) ? val : [val];
      values.forEach(function (v) {
        if (!v) return;
        var key = stepKey + '.' + v;
        var names = self.nodeMap[key] || [];
        names.forEach(function (n) { if (self.nodes[n]) self.nodes[n].visible = true; });
      });
    });

    // 3) Camera view (v45): keep a stack of selection keys in the order they
    // were chosen. The most recent one that defines a view wins; when it is
    // deselected the next most recent takes over; none = section default.
    var live = {};
    Object.keys(selections).forEach(function (stepKey) {
      var val = selections[stepKey];
      (Array.isArray(val) ? val : [val]).forEach(function (v) { if (v) live[stepKey + '.' + v] = true; });
    });
    this.viewStack = this.viewStack.filter(function (k) { return live[k]; });
    Object.keys(live).forEach(function (k) { if (self.viewStack.indexOf(k) < 0) self.viewStack.push(k); });
    var view = null;
    for (var i = this.viewStack.length - 1; i >= 0; i--) {
      if (this.viewMap && this.viewMap[this.viewStack[i]]) { view = this.viewMap[this.viewStack[i]]; break; }
    }
    this.activeView = view;

    // Visibility has settled. Only move the camera when what is on screen has
    // actually changed: a different model, a different set of visible nodes, or
    // a different option camera view. Choices with no 3D representation (food,
    // treats, protection, extras) leave the shopper's current angle and zoom
    // exactly as they left it.
    var signature = this.viewSignature();
    if (signature !== this._lastViewSignature) {
      this._lastViewSignature = signature;
      this.reframe();
    }
  };

  /* What the viewer is currently showing: model, visible toggle nodes, active view. */
  Viewer.prototype.viewSignature = function () {
    var self = this;
    var visible = [];
    (this.allToggleNames || []).forEach(function (name) {
      var node = self.nodes[name];
      if (node && node.visible) visible.push(name);
    });
    var view = this.activeView || null;
    var viewKey = view ? [view.zoom, view.azimuth, view.elevation].join('/') : '';
    return [this.currentUrl, visible.sort().join('|'), viewKey].join('::');
  };

  Viewer.prototype.destroy = function () {
    this.destroyed = true;
    this.stopLoop();
    this.cleanups.forEach(function (fn) { fn(); });
    this.cleanups = [];
    if (this.renderer) {
      this.renderer.dispose();
      if (this.renderer.domElement && this.renderer.domElement.parentNode) {
        this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
      }
    }
    if (this.pmrem) this.pmrem.dispose();
    if (this._bgTexture) this._bgTexture.dispose();
    Object.keys(this.modelCache).forEach(function (url) {
      // best-effort geometry/material disposal
    });
  };

  // ---- boot ----------------------------------------------------------------

  var registry = [];
  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-scpc-viewer]');
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      if (root.__scpc3d) continue;
      var v = new Viewer(root);
      if (v.mount) { root.__scpc3d = v; registry.push(v); }
    }
  }
  function destroyAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-scpc-viewer]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].__scpc3d) { roots[i].__scpc3d.destroy(); roots[i].__scpc3d = null; }
    }
  }

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { destroyAll(e.target); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  else initAll(document);

  window.SCConfigurator3D = { initAll: initAll, destroyAll: destroyAll };
}());